/** @jsxImportSource @emotion/react */
'use memo'

import styled from '@emotion/styled'
import type { ICellRendererParams } from 'ag-grid-community'
import colors from '@sportninja/common/constants/appColors'
import type { OrgRegistrationListRow } from '@sportninja/common/features/organization/registration/utils/listRows'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { useMeasuredRowHeight } from '../../hooks/useMeasuredRowHeight'
import OrgRegistrationDivisionGrid from '../OrgRegistrationDivisionGrid'
import OrgRegistrationVenueLink from '../OrgRegistrationVenueLink'

export interface OrgRegistrationDetailRowParams {
  /** Reports this row's content height, so the grid can size the row to it. */
  onHeight: (rowId: string, height: number) => void
}

type Props = ICellRendererParams<OrgRegistrationListRow> &
  OrgRegistrationDetailRowParams

/**
 * The full-width row under an expanded registration: the same divisions table
 * and checkout note the card opens — and, when the Location cell could only
 * say "+N more", every venue above it. Drawn across the grid's whole width, so it
 * stays in view while the columns above scroll sideways; on a narrow screen
 * the divisions table scrolls inside it instead.
 */
const OrgRegistrationDetailRow = ({ data, onHeight }: Props) => {
  'use memo'

  const ref = useMeasuredRowHeight<HTMLDivElement>((height) => {
    if (data) {
      onHeight(data.id, height)
    }
  })

  if (data?.kind !== 'detail') {
    return null
  }
  const { venues } = data.registration

  return (
    <div ref={ref}>
      {venues.length > MAX_CELL_VENUES && (
        <Venues aria-label={capitalizeWords('Locations')}>
          {venues.map((venue) => (
            <li key={venue.id}>
              <OrgRegistrationVenueLink venue={venue} withPlace />
            </li>
          ))}
        </Venues>
      )}
      <OrgRegistrationDivisionGrid
        divisions={data.registration.divisions}
        inactiveAvailabilityLabel={data.model.inactiveAvailabilityLabel}
      />
    </div>
  )
}

export default OrgRegistrationDetailRow

/** The Location cell shows up to two venues; past that, they are listed here. */
const MAX_CELL_VENUES = 2

const Venues = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  margin: 0;
  padding: 16px 24px 0;
  list-style: none;
  background: ${colors.SECONDARY_400};
  border-top: 1px solid ${colors.SECONDARY_200};
`
